import React, { useState, useRef, useEffect } from 'react';
import { labelByKey } from '@cm/new-shared-fe/services/labels';
import { getParameter } from '@cm/new-shared-fe/services/parameters';
import { Dropdown } from 'semantic-ui-react';
import { FormWrapper, Icon, InfoField, InputField, RadioButton, Button, formatIBAN, formatNRN, DatePicker, Checkbox } from '@cm/react-components';
import { useDatePicker } from '@cm/react-components/hooks';
import { validateIBAN } from '@cm/new-shared-fe/utils/validation/iban';
import { EMAIL } from '@cm/new-shared-fe/utils/validation/email';
import { PHONE } from '@cm/new-shared-fe/utils/validation/phone';
import { PHONE as formatPhone } from '@cm/new-shared-fe/utils/format/phone';
import { isValid, format } from 'date-fns';

import { Spinner } from './shared/spinner.components';

/* Helper functions to return the appropriate error objects for InputField */
const validateEmail = (email) => EMAIL(email) ? null : { email: true };
const validatePhone = (phone) => PHONE.BE(phone) ? null : { phone: true };
const validateIban = (iban) => {
  const regex = /[^a-zA-Z0-9]/g;
  const ibanWithoutSpaces = iban.replace(/\s/g, '');
  return !regex.test(ibanWithoutSpaces) && validateIBAN(iban) ? null : { iban: true };
};

export const Form = (props) => {
  const initialState = {
    firstName: props.user?.firstName,
    lastName: props.user?.lastName,
    memberIdentification: props.user?.NRN,
    email: '',
    bankAccount: '',
    phone: '',
    quality: '',
    paymentMethod: '',
    paymentFrequence: '',
    firstDayOfWork: undefined,
    insurance: { currentInsurance: props.user.insurance.currentInsurance, insuranceInterest: props.user.insurance.insuranceInterest },
    reasonOfInsuranceCancellation: '',
  };

  const [showMore, setShowMore] = useState({});
  const [data, setFormData] = useState(initialState);
  const [validationErrors, setValidationErrors] = useState([]);
  const [selectedDate, setSelectedDate] = useDatePicker(data.firstDayOfWork);
  const [showDateRequiredError, setShowDateRequiredError] = useState(false);
  const [showReasonOfCancellation, setShowReasonOfCancellation] = useState(false);

  const inputRefs = {
    bankAccount: useRef(),
    phone: useRef(),
    email: useRef(),
    qualityDateWrapper: useRef(),
    paymentMethod: useRef(),
    reasonOfInsuranceCancellation: useRef(),
  };

  useEffect(() => {
    if (window.location.search.includes('mockData=true')) {
      const mockDate = new Date('04/01/2022');
      const mockData = {
        email: 'johnny.bravo@cm.be',
        bankAccount: 'BE61817797883717',
        phone: '034252552',
        quality: 'CIVIL_SERVANT',
        paymentMethod: 'directDebitYearly',
        firstDayOfWork: format(mockDate, 'yyyy-MM-dd', { timeZone: 'UTC' }),
      };
      setSelectedDate(mockDate);
      setFormData(values => ({ ...values, ...mockData }));
    }
  }, [window.location]);

  useEffect(() => {
    if (validationErrors.includes('quality') || validationErrors.includes('firstDayOfWork')) {
      window.scrollTo(0, inputRefs.qualityDateWrapper.current.offsetTop);
      return;
    }
    if (validationErrors.includes('bankAccount')) {
      inputRefs.bankAccount.current.focus();
      return;
    }
    if (validationErrors.includes('paymentMethod')) {
      window.scrollTo(0, inputRefs.paymentMethod.current.offsetTop);
      return;
    }
    if (validationErrors.includes('phone')) {
      inputRefs.phone.current.focus();
      return;
    }
    if (validationErrors.includes('email')) {
      inputRefs.email.current.focus();
    }
  }, [validationErrors]);

  // --- Insurances part

  const [
    insuranceExclusions,
    insuranceSupportedCoverages,
  ] = [
    (getParameter('insurance_exclusions') || '').replace(/\s/g, '').split(','),
    (getParameter('insurance_coverages') || '').replace(/\s/g, '').split(','),
  ];

  /**
  * Get insurance exclusions rules
  * @returns {{[insurance:string]: string[]}} Exponential number of properties regarding number of exclusions
  */
  const getInsurancesExclusionRules = () => {
    return insuranceExclusions
      .reduce((result, current) => {
        if (!result[current]) {
          result[current] = [];
        }
        result[current] = result[current].concat(insuranceExclusions.filter(value => value !== current));
        return result;
      }, {});
  };

  /**
  * Define is an insurance should be excluded from the rendering regarding insurance exclusion rules
  * @param {string} insurance Insurance key
  * @returns {boolean}
  */
  const isExcludedInsurance = (insurance) => {
    const rules = getInsurancesExclusionRules();
    return insuranceExclusions.includes(insurance) && rules[insurance] && !(rules[insurance].reduce((result, current) => result && !data.insurance.currentInsurance[current], true));
  };

  /**
  * Apply a filter on insurance interest regarding current insurances
  * @param {string} insurance Insurance key
  * @returns {boolean}
  */
  const filterExclusiveInsurances = (insurance) => {
    if (Object.prototype.hasOwnProperty.call(data.insurance.currentInsurance, insurance) || isExcludedInsurance(insurance)) {
      return false;
    }
    return true;
  };

  /**
  * Get insurance interest to reset regarding exclusion rules
  * @param {string} insurance Insurance key
  * @returns {false|string[]}
  */
  const getInsuranceInterestsToReset = (insurance) => {
    const rules = getInsurancesExclusionRules();
    return insuranceExclusions.includes(insurance) && rules[insurance];
  };

  /**
   *
   * @param {*} insurance
   */
  const resetInsuranceInterests = (insurance) => {
    const insuranceInterestsToReset = getInsuranceInterestsToReset(insurance);
    if (insuranceInterestsToReset) {
      insuranceInterestsToReset.forEach(insurance => {
        if (data.insurance.insuranceInterest[insurance]) {
          data.insurance.insuranceInterest[insurance] = false;
        }
      });
    }
  };

  /**
  * Generate radio button handler current insurances
  * @param {*} value
  * @param {*} insurance
  * @returns {void}
  */
  const handleInsuranceChoice = (value, insurance) => () => {
    const currentInsurance = data.insurance.currentInsurance;
    currentInsurance[insurance] = value;
    setFormData(values => ({ ...values, ...{ insurance: { currentInsurance, insuranceInterest: values.insurance.insuranceInterest } }, reasonOfInsuranceCancellation: hasCancellation() ? values.reasonOfInsuranceCancellation : '' }));
    setShowReasonOfCancellation(hasCancellation());
    // TODO: Kayvine: Do the checkboxes ever need to be updated here? I don't understand the requirement here.
    resetInsuranceInterests(insurance);
  };

  /**
  * Generate checkbox button insurance interest handler
  * @param {*} event
  * @param {*} insurance
  * @returns {void}
  */
  const handleInsuranceInterestChange = (event, insurance) => {
    const insuranceInterestsToReset = getInsuranceInterestsToReset(insurance);

    setFormData(prevState => {
      const insuranceInterest = { ...prevState.insurance.insuranceInterest };
      insuranceInterest[insurance] = event.target.checked;
      if (insuranceInterestsToReset) {
        insuranceInterestsToReset.forEach(insurance => {
          if (insuranceInterest[insurance]) {
            insuranceInterest[insurance] = false;
          }
        });
      }
      return { ...prevState, insurance: { ...prevState.insurance, insuranceInterest } };
    });
  };

  /**
  * Indicate if some insurance are canceled
  * @returns {boolean}
  */
  const hasCancellation = () => {
    return !Object.values(data.insurance.currentInsurance).reduce((result, current) => result && current, true);
  };

  // ---- End insurance part

  const statutes = [
    {
      key: 'Bediende',
      value: 'EMPLOYEE',
      text: labelByKey('data.statute.employee'),
    },
    {
      key: 'Arbeider',
      value: 'LABOURER',
      text: labelByKey('data.statute.laborer'),
    },
    {
      key: 'Ambtenaar',
      value: 'CIVIL_SERVANT',
      text: labelByKey('data.statute.servant'),
    },
  ];

  const PAYMENT_MODES = {
    DDQ: 'directDebitQuarterly',
    DDY: 'directDebitYearly',
    TRANSFER: 'transfer',
  };

  const PAYMENT_MAP = {
    [PAYMENT_MODES.DDY]: {
      PAYMENT_METHOD: 'DIRECT_DEBIT',
      PAYMENT_FREQUENCE: 'YEARLY',
    },
    [PAYMENT_MODES.DDQ]: {
      PAYMENT_METHOD: 'DIRECT_DEBIT',
      PAYMENT_FREQUENCE: 'QUARTERLY',
    },
    [PAYMENT_MODES.TRANSFER]: {
      PAYMENT_METHOD: 'TRANSFER',
      PAYMENT_FREQUENCE: 'YEARLY',
    },
  };

  const getFormErrors = () => {
    const errors = [];

    if (!data.email || validateEmail(data.email)) {
      errors.push('email');
    }
    if (!data.bankAccount || !validateIBAN(data.bankAccount)) {
      errors.push('bankAccount');
    }
    if (!data.phone || validatePhone(data.phone)) {
      errors.push('phone');
    }
    if (data.quality === '') {
      errors.push('quality');
    }
    if (data.paymentMethod === '') {
      errors.push('paymentMethod');
    }
    if (!isValid(new Date(data.firstDayOfWork))) {
      errors.push('firstDayOfWork');
    }
    console.log('errors: ', errors);
    return errors;
  };

  const handleChange = (event) => {
    setFormData(values => ({ ...values, [event.target.name]: event.target.value }));
  };

  const handleQualityDropdownChange = (event, { value }) => {
    setFormData(values => ({ ...values, quality: value }));
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setFormData(values => ({ ...values, firstDayOfWork: format(date, 'yyyy-MM-dd', { timeZone: 'UTC' }) }));
  };

  const handleSubmit = () => {
    const formErrors = getFormErrors();
    setValidationErrors(formErrors);
    if (formErrors.length > 0) {
      handleErrorLabelsOnSubmit(formErrors);
      return;
    }

    const payload = {
      ...data,
      ...{
        paymentMethod: PAYMENT_MAP[data.paymentMethod].PAYMENT_METHOD,
        paymentFrequence: PAYMENT_MAP[data.paymentMethod].PAYMENT_FREQUENCE,
      },
    };

    props.onSubmit(payload);
  };

  const handleErrorLabelsOnSubmit = (formErrors) => {
    const inputsToFocusBlur = ['phone', 'email', 'bankAccount'];
    inputsToFocusBlur.forEach((input) => {
      inputRefs[input].current.focus();
      inputRefs[input].current.blur();
    });
    if (formErrors.includes('firstDayOfWork')) {
      setShowDateRequiredError(true);
    }
  };

  const handleShowMore = (type) => {
    setShowMore(value => ({ ...value, [type]: !showMore[type] }));
  };

  return <>
    <div className="mb-3">
      <h2 className="text-3xl font-header">{labelByKey('data.personal')}</h2>
      <hr className="hr-green"/>
    </div>

    <div className="flex flex-col w-10/12 px-0 ml-form md:w-full md:ml-0 md:px-0 leading-[19px]">
      <div className="mb-3 block border-b border-dotted border-griege">
        <div className="mb-3">
          <FormWrapper icon="user" label={labelByKey('data.identification')}>
            <span data-e2e="data.field.name">{props.user?.firstName} {props.user?.lastName}</span>
          </FormWrapper>
          <FormWrapper icon="rnr" label={labelByKey('data.nrn')}>
            <span data-e2e="data.field.nrn">{formatNRN(props.user?.NRN)}</span>
          </FormWrapper>
        </div>
      </div>
      <div className="mb-3 block border-b border-dotted border-griege">
        <div className="mb-3">
          <style>{'.ui.selection.dropdown.active, .ui.selection.dropdown.visible {z-index: 20;}'}</style>
          <FormWrapper icon="small-building" label={<>{labelByKey('data.work.info')} <Icon name="form-help" className="w-5 h-5 cursor-pointer" onClick={() => handleShowMore('statute')}/></>}>
          {showMore.statute && <InfoField className="mb-1.5">{labelByKey('statute.extra.info')}</InfoField>}
            <div ref={inputRefs.qualityDateWrapper}>
              {/* TODO: Refactor dropdown to react-components */}
              <Dropdown
                key={data.quality}
                placeholder={`${labelByKey('data.work.info')} *`}
                fluid
                selection
                options={statutes}
                onChange={handleQualityDropdownChange}
                value={data.quality}
                name='quality'
                data-e2e="data.field.statute"
              />
              {validationErrors.includes('quality') && !data.quality && <InfoField error className="my-1.5" data-e2e="form.submit.error">{labelByKey('input_field.error.required')}</InfoField>}
              <DatePicker
                className="mt-2.5"
                showRequiredError={showDateRequiredError}
                locale={document.documentElement.lang}
                value={selectedDate}
                onSelect={handleDateChange}
                required
                label={labelByKey('data.firstDayOfWork.required')}
                shadowDom
                data-e2e="data.field.date"
              />
            </div>
          </FormWrapper>
          <FormWrapper icon="financial-overview" label={<>{labelByKey('data.iban')} <Icon name="form-help" className="w-5 h-5 cursor-pointer" onClick={() => handleShowMore('iban')}/></>} data-e2e="section.IBAN">
            {showMore.iban && <InfoField className="mb-1.5">{labelByKey('iban.extra.info')}</InfoField>}
            <InputField
              ref={inputRefs.bankAccount}
              required
              label={labelByKey('data.iban.required')}
              onChange={handleChange}
              formatter={formatIBAN}
              validate={validateIban}
              name='bankAccount'
              value={data.bankAccount}
              data-e2e="data.field.bankAccount"
            />
          </FormWrapper>
          <FormWrapper icon="bijdragen" label={<>{labelByKey('data.payment')} <Icon name="form-help" className="w-5 h-5 cursor-pointer" onClick={() => handleShowMore('payment')} data-e2e="PAYMENT.form-info.button"/></>}>
            {showMore.payment &&
            <InfoField className="mb-1.5">
              {labelByKey('payment.extra.info')}
              <br/>
              <span className="text-green hover:underline" dangerouslySetInnerHTML={{ __html: labelByKey('payment.extra.info.link') }}/>
            </InfoField>}
            <div className="flex flex-col" data-e2e="data.field.payment" ref={inputRefs.paymentMethod}>
              <RadioButton checked={data.paymentMethod === PAYMENT_MODES.DDQ} value={PAYMENT_MODES.DDQ} label={labelByKey('data.payment.option1')} name="paymentMethod" onChange={handleChange} />
              <RadioButton checked={data.paymentMethod === PAYMENT_MODES.DDY} value={PAYMENT_MODES.DDY} label={labelByKey('data.payment.option2')} name="paymentMethod" onChange={handleChange} />
              <RadioButton checked={data.paymentMethod === PAYMENT_MODES.TRANSFER} value={PAYMENT_MODES.TRANSFER} label={labelByKey('data.payment.option3')} name="paymentMethod" onChange={handleChange} />
            </div>
            {validationErrors.includes('paymentMethod') && !data.paymentMethod && (
              <InfoField error className="mt-1.5" data-e2e="data.field.payment.error">
                {labelByKey('radio.error.required')}
              </InfoField>
            )}
          </FormWrapper>

          {props.insuranceIsActiveOnDomain && (
            <>
              {Object.keys(data.insurance.currentInsurance).length > 0 && (
                <FormWrapper icon="health-coach" label={labelByKey('insurances.current.title')}>
                  <div className="" data-e2e="data.insurances.container">
                    <div className="pl-0 pr-0">
                      <div>
                        <p className="text-md mb-2">{labelByKey('insurances.current.resume', { coverages: Object.keys(data.insurance.currentInsurance).map(insurance => labelByKey(`insurances.labels.${insurance.toUpperCase()}`)).join(',') })}</p>
                      </div>
                      <div className="relative overflow-x-auto">
                        <table className="w-full text-left">
                          <thead className="text-sm">
                            <tr>
                              <th scope="col" className="px-6 py-3">
                              </th>
                              <th scope="col" className="px-6 py-3 text-center">
                                {labelByKey('insurances.current.status.uninterupted')}
                              </th>
                              <th scope="col" className="px-6 py-3 text-center">
                                {labelByKey('insurances.current.status.stop')}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {
                              Object.keys(data.insurance.currentInsurance).map((insurance, idx) => {
                                return (
                                  <tr key={`insurance-line-${idx}`} className={`bg-white ${Object.keys(data.insurance.currentInsurance).length > (idx + 1) ? 'border-b border-solid border-griege' : ''}`}>
                                    <th scope="row" className="px-4 py-2 text-md font-bold whitespace-nowrap">
                                      {labelByKey(`insurances.labels.${insurance.toUpperCase()}`)}
                                    </th>
                                    <td className="px-4 py-2 text-center cm-radio">
                                      <label className="cm-radio">
                                        <input className="radio" type="radio" checked={data.insurance.currentInsurance[insurance]} name={`insurance-${insurance}`} onChange={handleInsuranceChoice(true, insurance)} data-e2e={`data.field.${insurance}.continue`} />
                                        <span className="radio-custom cursor-pointer"></span>
                                      </label>
                                    </td>
                                    <td className="px-4 py-2 text-center">
                                      <label className="cm-radio">
                                        <input className="radio" type="radio" name={`insurance-${insurance}`} onChange={handleInsuranceChoice(false, insurance)} data-e2e={`data.field.${insurance}.stop`} />
                                        <span className="radio-custom cursor-pointer"></span>
                                      </label>
                                    </td>
                                  </tr>
                                );
                              })
                            }
                          </tbody>
                        </table>
                      </div>
                    </div>
                    {showReasonOfCancellation && (
                      <div className="pl-0 pr-0 reden mt-2">
                        <InputField
                          ref={inputRefs.reasonOfInsuranceCancellation}
                          label={labelByKey('insurances.current.cancel.reason')}
                          onChange={handleChange}
                          name='reasonOfInsuranceCancellation'
                          value={data.reasonOfInsuranceCancellation}
                          data-e2e="data.field.reasonOfCancellation"
                        />
                      </div>
                    )}
                  </div>
                </FormWrapper>
              )}
              {insuranceSupportedCoverages && Object.values(insuranceSupportedCoverages).filter((insurance) => filterExclusiveInsurances(insurance) && insurance !== 'hf').length > 0 && (
                <FormWrapper icon="form-information" label={labelByKey('insurances.interest.title')}>
                  <div className="table-cell" data-e2e="data.interest.container">
                    <div className="pl-0 pr-0 pt-1">
                      {Object.values(insuranceSupportedCoverages)
                        .filter((insurance) => filterExclusiveInsurances(insurance) && insurance !== 'hf')
                        .map((insurance, idx) => {
                          return (
                            <div key={`insurance-interest-${idx}`}>
                              <div className="mb-1.5 relative">
                                <Checkbox
                                  checked={data.insurance.insuranceInterest?.[insurance] || false}
                                  label={labelByKey(`insurances.labels.${insurance.toUpperCase()}`)}
                                  name={`insurance-interest-${insurance}`}
                                  onChange={(e) => handleInsuranceInterestChange(e, insurance)}
                                  data-e2e={`insurance-interest-${insurance}`}
                                />
                                <p className="inline-block text-md mt-1 ml-[28px]">
                                  <span dangerouslySetInnerHTML={{
                                    __html: labelByKey(`insurances.descriptions.${insurance.toUpperCase()}`, {
                                      link: `<a class="text-green hover:underline" target="_blank" href="${labelByKey(`insurances.links.${insurance.toUpperCase()}`)}">${labelByKey('insurances.links.more')}</a>`,
                                    }),
                                  }}>
                                  </span>
                                </p>
                              </div>
                            </div>
                          );
                        })
                      }
                    </div>
                  </div>
                </FormWrapper>
              )}
            </>
          )}

          <FormWrapper icon="phone" label={labelByKey('data.contact')}>
            <InputField
              ref={inputRefs.phone}
              required={true}
              label={labelByKey('data.phone.required')}
              onChange={handleChange}
              formatter={formatPhone.BE}
              validate={validatePhone}
              name='phone'
              value={data.phone}
              data-e2e="data.field.phone"
            />
          </FormWrapper>
          <FormWrapper icon="mail">
            <InputField
              ref={inputRefs.email}
              required={true}
              label={labelByKey('data.email.required')}
              onChange={handleChange}
              validate={validateEmail}
              name='email'
              value={data.email}
              data-e2e="data.field.email"
            />
            <InfoField className="mt-2.5">
              {labelByKey('personal.info.email')}
              <Icon className="w-5 h-5 ml-1.5 cursor-pointer" name="form-information" onClick={() => handleShowMore('email')} data-e2e="email.form-info.button"/>
            </InfoField>
            {showMore.email && <InfoField className="mt-1.5" icon="form-information">{labelByKey('personal.info.email.extra')}</InfoField>}
          </FormWrapper>
          <div>
            <ul className="text-sm">
              <li>⁽¹⁾ <span className="text-green hover:text-green-dark hover:underline" dangerouslySetInnerHTML={{ __html: labelByKey('privacy.policy.link') }}/></li>
              <li>⁽²⁾ {labelByKey('privacy.policy.exception')}</li>
            </ul>
          </div>
        </div>
      </div>

      {data.paymentMethod && data.paymentMethod !== PAYMENT_MODES.TRANSFER && <InfoField info className="mb-3" data-e2e="form.direct_debit.info_field">{labelByKey('form.direct_debit.signing')}</InfoField>}

      {props.error && <InfoField error className="mb-3">{labelByKey('form.service.error')}</InfoField>}

      <Button className="ml-auto" onClick={handleSubmit} disabled={props.loading} data-e2e="form.submit.button">
        {labelByKey('confirm.button')}
        {props.loading && <Spinner/>}
      </Button>
    </div>
  </>;
};
